import React from "react";
import { useEffect, useState } from "react";
import {
  Navigate,
  Route,
  Routes,
  useNavigate,
  useLocation
} from "react-router-dom";

import { useAuthorizeSlack, useCurrentUser } from "../app/appHooks";
import AppLayout from "./privateRoutes/AppLayout";

import { PATH_NAME } from "routes/pathNames";
import * as LazyComponent from "utils/lazyComponents";
import LandingPage from "pages/landingPage";
import RequireAuth from "./privateRoutes/RequireAuth";
import SuperAdminLayout from "./superAdminRoutes/superAdminLogin";
import { useIsAdminLoggedIn } from "./superAdminRoutes/hooks";

/** Legacy /app/settings redirect — sends Stripe returns to Billing, everything else to Workspace */
const SettingsRedirect = () => {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  // Stripe payment returns have ?status= — send those to Billing
  if (params.has("status")) {
    return <Navigate replace to={`${PATH_NAME.BILLING}${search}`} />;
  }
  // Old tab=3 (subscription) links → Billing
  if (params.get("tab") === "3") {
    return <Navigate replace to={PATH_NAME.BILLING} />;
  }
  // Everything else → Workspace (preserve query params)
  return <Navigate replace to={`${PATH_NAME.WORKSPACE}${search}`} />;
};

const AppRoutes = () => {
  // Handle direct token from URL synchronously in useState initializer
  const [tokenFetched, setTokenFetched] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get("token");
    if (urlToken) {
      localStorage.setItem("token", urlToken);
      // Clean up URL without the token parameter
      window.history.replaceState({}, document.title, "/app");
      return true;
    }
    return !!localStorage.getItem("token");
  });
  const adminLoggedIn = useIsAdminLoggedIn();

  const { data } = useAuthorizeSlack(setTokenFetched);

  const { data: authenticatedUser } = useCurrentUser(tokenFetched, false);

  const navigate = useNavigate();
  useEffect(() => {
    if ((data as any)?.error) {
      navigate(PATH_NAME.LOGIN, {
        state: { error: (data as any)?.error }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);
  return (
    <Routes>
      {/* Super Admin routes ---- DISABLED TILL PRODUCTION */}
      {adminLoggedIn ? (
        <Route element={<SuperAdminLayout />}>
          <Route
            path={PATH_NAME.ADMIN}
            element={<LazyComponent.SuperAdminPanel />}
          />
          <Route
            path="/*"
            element={<Navigate replace to={PATH_NAME.ADMIN} />}
          />
        </Route>
      ) : (
        <>
          <Route
            path={`${PATH_NAME.ADMIN}-login`}
            element={<LazyComponent.SuperAdminSignIn />}
          />
        </>
      )}
      {tokenFetched && (data?.user || authenticatedUser) ? (
        //Authorized user routes
        <Route element={<AppLayout />}>
          <Route
            path={PATH_NAME.HOME_PAGE}
            element={<LazyComponent.NewFeeds />}
          />
          <Route
            path={PATH_NAME.NEWS_FEED}
            element={<LazyComponent.NewFeeds />}
          />

          <Route
            path={PATH_NAME.LEADERBOARD}
            element={<LazyComponent.Leaderboard />}
          />

          <Route
            path={`${PATH_NAME.ACCOUNT}/:userId`}
            element={<LazyComponent.Account />}
          />

          <Route
            path={PATH_NAME.CUSTOMIZATION}
            element={<LazyComponent.Customization />}
          />

          {/* Actions/Awards feature - available to all users */}
          <Route
            path={PATH_NAME.ACTIONS}
            element={<LazyComponent.ActionsPage />}
          />
          <Route
            path={PATH_NAME.SUBMIT_CLAIM}
            element={<LazyComponent.SubmitClaimPage />}
          />
          <Route
            path={PATH_NAME.MY_CLAIMS}
            element={<LazyComponent.MyClaimsPage />}
          />

          {/* company admin Routes */}
          <Route
            element={
              <RequireAuth allowedRoles={["COMPANY_ADMIN"]}></RequireAuth>
            }
          >
            {/* <Route
              path={PATH_NAME.INSIGHTS}
              element={<LazyComponent.Insights />}
            /> */}
            <Route
              path={PATH_NAME.OVERVIEW_DASHBOARD}
              element={<LazyComponent.OverviewDashboard />}
            />
            <Route
              path={PATH_NAME.EMPLOYEE_INSIGHTS}
              element={<LazyComponent.EmployeeInsights />}
            />
            <Route
              path={PATH_NAME.TEAM_ANALYSIS}
              element={<LazyComponent.TeamAnalysis />}
            />
            <Route
              path={PATH_NAME.RECOGNITION_TRENDS}
              element={<LazyComponent.RecognitionTrends />}
            />
            <Route
              path={PATH_NAME.LOW_ENGAGEMENT}
              element={<LazyComponent.LowEngagement />}
            />
            <Route
              path={PATH_NAME.WORKSPACE}
              element={<LazyComponent.Settings />}
            />
            <Route
              path={PATH_NAME.BILLING}
              element={<LazyComponent.Billing />}
            />
            {/* Legacy redirects */}
            <Route path={PATH_NAME.SETTINGS} element={<SettingsRedirect />} />
            <Route
              path={PATH_NAME.ADMIN_CLAIMS}
              element={<Navigate replace to={`${PATH_NAME.WORKSPACE}?tab=1`} />}
            />
          </Route>

          <Route
            path={PATH_NAME.HELP_CENTER}
            element={<LazyComponent.HelpCenter />}
          />
          <Route
            path="/*"
            element={<Navigate replace to={PATH_NAME.HOME_PAGE} />}
          />
        </Route>
      ) : (
        //Unauthorized user routes
        <>
          <Route path={PATH_NAME.LOGIN} element={<LazyComponent.SignIn />} />
          <Route path="/*" element={<LazyComponent.SignIn />} />
        </>
      )}
      {/*Public Routes*/}
      <Route path={PATH_NAME.LANDING_PAGE} element={<LandingPage />} />
      {/* <Route
        path={`${PATH_NAME.PUBLIC_PROFILE}/:userId`}
        element={<LazyComponent.PublicProfile />}
      /> */}
      <Route
        path={PATH_NAME.PRIVACY_POLICY}
        element={<LazyComponent.PrivacyPolicy />}
      />
      <Route
        path={PATH_NAME.TERMS_OF_SERVICE}
        element={<LazyComponent.TermsOfService />}
      />
    </Routes>
  );
};

export default AppRoutes;
